import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, KeyboardAvoidingView, Platform } from 'react-native';
import { InsetModal as Modal } from './InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { colors, typography, spacing } from '../theme';
import { RecentPost } from '../screens/dashboard/RecentPostsCard';
import { PostCommentsPanel } from './PostCommentsPanel';
import { useThemedStyles } from '../hooks/useThemedStyles';

export interface PostCommentsModalProps {
  visible: boolean;
  post: RecentPost | null;
  pageId: string;
  onClose: () => void;
}

/** Bottom-sheet wrapper around PostCommentsPanel — used on mobile/compact
 * layouts where there's no room for a persistent right pane (see
 * PostsListScreen's right pane on web/wide layouts for the non-modal case). */
export const PostCommentsModal: React.FC<PostCommentsModalProps> = ({ visible, post, pageId, onClose }) => {
  const styles = useThemedStyles(getStyles);
  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <View style={styles.overlay}>
        <KeyboardAvoidingView
          style={styles.sheet}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
          <View style={styles.header}>
            <Text style={styles.headerTitle} numberOfLines={1}>{post?.message || 'Post'}</Text>
            <TouchableOpacity onPress={onClose} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Ionicons name="close" size={22} color={colors.text.primary as string} />
            </TouchableOpacity>
          </View>
          {post && <PostCommentsPanel post={post} pageId={pageId} />}
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'flex-end',
  },
  sheet: {
    height: '80%',
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border as string,
  },
  headerTitle: {
    flex: 1,
    fontSize: typography.fontSize.base,
    fontWeight: '600' as const,
    color: colors.text.primary as string,
    marginRight: spacing.sm,
  },
});
